// Context menu and popover menu: right-click a row, or open from a button. Submenus open on hover, →
// or Enter; ↑ ↓ move, ← closes a submenu, Escape closes. Positioned in the viewport, flipped to fit.
// An entry with keepOpen (a multi-select filter) toggles without closing the menu.
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronRight } from 'lucide-react';
import { cx } from './cx';

export type MenuEntry =
  | { kind?: 'item'; label: ReactNode; icon?: ReactNode; onSelect?: () => void; items?: MenuEntry[]; danger?: boolean; checked?: boolean; disabled?: boolean; keepOpen?: boolean }
  | { kind: 'header'; label: ReactNode }
  | { kind: 'separator' };

type Item = Extract<MenuEntry, { label: ReactNode; kind?: 'item' }>;
const isItem = (e: MenuEntry): e is Item => e.kind === undefined || e.kind === 'item';

/** `anchor`: the button that opened the menu. A mousedown on it is left to its own click, so it can toggle the menu shut. */
export function Menu({ x, y, entries, onClose, label, anchor }: {
  x: number; y: number; entries: MenuEntry[]; onClose: () => void; label: string; anchor?: HTMLElement | null;
}) {
  useEffect(() => {
    const down = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (!t.closest('[data-menu]') && !anchor?.contains(t)) onClose();
    };
    const scroll = () => onClose();
    window.addEventListener('mousedown', down);
    window.addEventListener('resize', scroll);
    window.addEventListener('blur', scroll);
    return () => { window.removeEventListener('mousedown', down); window.removeEventListener('resize', scroll); window.removeEventListener('blur', scroll); };
  }, [onClose, anchor]);
  return createPortal(<Panel x={x} y={y} entries={entries} onClose={onClose} label={label} depth={0} />, document.body);
}

function Panel({ x, y, flipX, entries, onClose, label, depth, onBack }: {
  x: number; y: number; flipX?: number; entries: MenuEntry[]; onClose: () => void; label: string; depth: number; onBack?: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });
  const [active, setActive] = useState(-1);
  const [open, setOpen] = useState<{ index: number; x: number; y: number; flipX: number; focus: boolean } | null>(null);
  const items = entries.map((e, i) => (isItem(e) && !e.disabled ? i : -1)).filter((i) => i >= 0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // A submenu that would run off the right edge opens to the left of its parent instead.
    const left = x + r.width > window.innerWidth - 8 ? Math.max(8, (flipX ?? x) - r.width) : x;
    const top = y + r.height > window.innerHeight - 8 ? Math.max(8, window.innerHeight - r.height - 8) : y;
    setPos({ left, top });
    if (depth === 0) el.focus();
  }, [x, y, flipX, depth]);

  const openSub = (index: number, focus: boolean) => {
    const node = ref.current?.querySelector<HTMLElement>(`[data-index="${index}"]`);
    if (!node) return;
    const r = node.getBoundingClientRect();
    setOpen({ index, x: r.right - 4, y: r.top - 6, flipX: r.left + 4, focus });
  };
  const choose = (index: number) => {
    const e = entries[index];
    if (!e || !isItem(e) || e.disabled) return;
    if (e.items) { openSub(index, true); return; }
    e.onSelect?.();
    if (!e.keepOpen) onClose();
  };
  const onKey = (ev: React.KeyboardEvent) => {
    if (open?.focus) return;
    const at = items.indexOf(active);
    if (ev.key === 'ArrowDown') { ev.preventDefault(); setActive(items[(at + 1) % items.length] ?? -1); }
    else if (ev.key === 'ArrowUp') { ev.preventDefault(); setActive(items[(at - 1 + items.length) % items.length] ?? -1); }
    else if (ev.key === 'ArrowRight' || ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); if (active >= 0) choose(active); }
    else if (ev.key === 'ArrowLeft' && onBack) { ev.preventDefault(); onBack(); }
    else if (ev.key === 'Escape') { ev.preventDefault(); ev.stopPropagation(); if (onBack) onBack(); else onClose(); }
    else if (ev.key === 'Tab') { ev.preventDefault(); onClose(); }
  };
  useEffect(() => { if (depth > 0 && items.length) { setActive(items[0]!); ref.current?.focus(); } }, [depth]); // eslint-disable-line react-hooks/exhaustive-deps

  const sub = open ? entries[open.index] : null;
  return (
    <>
      <div
        ref={ref}
        data-menu
        role="menu"
        aria-label={label}
        tabIndex={-1}
        onKeyDown={onKey}
        onContextMenu={(e) => e.preventDefault()}
        style={{ left: pos.left, top: pos.top }}
        className="fixed z-[60] min-w-[230px] max-w-[320px] rounded-xl border border-line bg-white p-1.5 text-[13px] leading-tight shadow-pop outline-none"
      >
        {entries.map((e, i) => {
          if (e.kind === 'separator') return <div key={i} role="separator" className="mx-0.5 my-1 h-px bg-line-soft" />;
          if (e.kind === 'header') return <div key={i} className="px-2.5 pb-1 pt-1.5 text-[11px] font-bold uppercase tracking-[0.05em] text-muted">{e.label}</div>;
          const on = i === active || open?.index === i;
          return (
            <div
              key={i}
              data-index={i}
              role={e.checked === undefined ? 'menuitem' : e.keepOpen ? 'menuitemcheckbox' : 'menuitemradio'}
              aria-checked={e.checked}
              aria-haspopup={e.items ? 'menu' : undefined}
              aria-expanded={e.items ? open?.index === i : undefined}
              aria-disabled={e.disabled || undefined}
              onMouseEnter={() => { setActive(i); if (e.items) openSub(i, false); else setOpen(null); }}
              onClick={() => choose(i)}
              className={cx('flex min-h-8 cursor-pointer select-none items-center gap-2.5 whitespace-nowrap rounded-lg px-2.5',
                e.disabled ? 'cursor-default text-muted' : e.danger ? 'text-bad' : 'text-ink', on && !e.disabled && 'bg-brand-soft', on && !e.danger && !e.disabled && 'text-brand')}
            >
              {e.checked !== undefined
                ? <span className="flex w-4 flex-none justify-center">{e.checked && <Check aria-hidden size={14} strokeWidth={2.6} />}</span>
                : e.icon && <span className="flex w-4 flex-none justify-center text-current opacity-80">{e.icon}</span>}
              <span className="min-w-0 flex-1 truncate">{e.label}</span>
              {e.items && <ChevronRight aria-hidden size={14} className="text-muted" />}
            </div>
          );
        })}
      </div>
      {sub && isItem(sub) && sub.items && open && (
        <Panel x={open.x} y={open.y} flipX={open.flipX} entries={sub.items} onClose={onClose} label={String(typeof sub.label === 'string' ? sub.label : label)} depth={depth + 1}
          onBack={() => { setOpen(null); ref.current?.focus(); }} />
      )}
    </>
  );
}

/** A button that opens a menu under itself. */
export function MenuButton({ label, entries, children, className, title }: { label: string; entries: MenuEntry[] | (() => MenuEntry[]); children: ReactNode; className?: string; title?: string }) {
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  return (
    <>
      <button
        ref={btn}
        type="button"
        aria-haspopup="menu"
        aria-expanded={at !== null}
        aria-label={title ? undefined : label}
        title={title}
        onClick={(e) => {
          e.stopPropagation();
          const r = e.currentTarget.getBoundingClientRect();
          setAt(at ? null : { x: r.left, y: r.bottom + 4 });
        }}
        className={className}
      >
        {children}
      </button>
      {at && <Menu x={at.x} y={at.y} anchor={btn.current} label={label} entries={typeof entries === 'function' ? entries() : entries} onClose={() => { setAt(null); btn.current?.focus(); }} />}
    </>
  );
}
